import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Seo, SITE_URL } from "@/components/Seo";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import {
  ChevronRight,
  BadgeEuro,
  MapPin,
  ShieldCheck,
  Leaf,
  Wallet,
  Wrench,
  ThermometerSun,
  PhoneCall,
  ClipboardCheck,
  Sparkles,
  Quote,
  Star,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/13041129/pexels-photo-13041129.jpeg";
const ENGINEER_IMG = "https://images.pexels.com/photos/6471913/pexels-photo-6471913.jpeg";
const FAMILY_IMG = "https://images.pexels.com/photos/3875141/pexels-photo-3875141.jpeg";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1 } }),
};

const benefits = [
  { icon: Wallet, title: "Bis zu 70% Förderung", text: "Wir sichern Ihnen alle staatlichen Zuschüsse (BAFA & KfW) und kümmern uns um den Antrag." },
  { icon: Leaf, title: "Klimafreundlich heizen", text: "Reduzieren Sie Ihren CO₂-Ausstoß um bis zu 90% gegenüber Öl und Gas." },
  { icon: ThermometerSun, title: "Behaglich das ganze Jahr", text: "Wärmepumpen heizen im Winter und kühlen im Sommer – auf Wunsch." },
  { icon: Wallet, title: "Heizkosten senken", text: "Sparen Sie langfristig hunderte Euro pro Jahr gegenüber fossilen Brennstoffen." },
];

const steps = [
  { icon: ClipboardCheck, title: "1. Anfrage stellen", text: "Beantworten Sie ein paar kurze Fragen zu Ihrem Gebäude – in unter 2 Minuten." },
  { icon: PhoneCall, title: "2. Kostenlose Beratung", text: "Ein Fachpartner aus Ihrer Region meldet sich mit einer ersten Einschätzung." },
  { icon: Wrench, title: "3. Festpreis-Installation", text: "Wir installieren Ihre Wärmepumpe sauber, schnell und zum garantierten Festpreis." },
];

const testimonials = [
  { name: "Familie Berger", city: "München", text: "Von der Anfrage bis zur Installation lief alles reibungslos. Die Förderung wurde komplett für uns übernommen!" },
  { name: "Thomas K.", city: "Köln", text: "Endlich raus aus der alten Ölheizung. Unsere Heizkosten haben sich fast halbiert. Top Beratung." },
  { name: "Sandra M.", city: "Hamburg", text: "Sehr professionelles Team, faire Festpreise und super freundlich. Klare Empfehlung." },
];

const faqs = [
  {
    q: "Was kostet eine Wärmepumpe inklusive Installation?",
    a: "Je nach Gebäude, Wärmebedarf und Wärmepumpen-Typ liegen die Gesamtkosten meist zwischen 15.000 € und 35.000 €. Durch die staatliche Förderung von bis zu 70% sinkt Ihr Eigenanteil deutlich. Ihr genaues Festpreis-Angebot erhalten Sie kostenlos über unser 2-Minuten-Formular.",
  },
  {
    q: "Wie hoch ist die Wärmepumpen-Förderung 2026?",
    a: "Über die Bundesförderung für effiziente Gebäude (BEG) sind bis zu 70% Zuschuss möglich: Grundförderung (30%), Klimageschwindigkeits-Bonus (20%) und Einkommens-Bonus (30%) lassen sich kombinieren. Wir prüfen Ihren Anspruch und übernehmen die komplette Antragstellung.",
  },
  {
    q: "Eignet sich meine Immobilie (auch Altbau) für eine Wärmepumpe?",
    a: "In den allermeisten Fällen ja. Moderne Wärmepumpen erreichen auch in sanierten und vielen unsanierten Altbauten hohe Effizienz – oft sogar mit den vorhandenen Heizkörpern. Unsere Fachpartner prüfen Ihre Bausubstanz individuell vor Ort.",
  },
  {
    q: "Wie lange dauert die Installation einer Wärmepumpe?",
    a: "Nach Ihrer Anfrage meldet sich ein lokaler Fachpartner innerhalb von 24 Stunden. Die eigentliche Installation ist in der Regel in 1–3 Tagen abgeschlossen.",
  },
  {
    q: "Kann ich die Wärmepumpe mit Photovoltaik kombinieren?",
    a: "Ja. Die Kombination aus Wärmepumpe und PV-Anlage senkt Ihre Betriebskosten zusätzlich, da Sie einen Teil des Stroms selbst erzeugen. Gerne beraten wir Sie zu einer sinnvollen Gesamtlösung.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

function Header() {
  return (
    <header className="absolute top-0 left-0 right-0 z-30" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/waermekraft-logo.png" alt="Wärmekraft" className="h-9 w-auto" />
        </div>
        <Link
          to="/funnel"
          data-testid="header-cta"
          className="hidden sm:inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand transition-colors"
        >
          Angebot einholen <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </header>
  );
}

export default function Landing() {
  return (
    <div className="bg-bone min-h-screen overflow-x-hidden">
      <Seo
        title="Wärmepumpe mit bis zu 70% Förderung | Wärmekraft"
        description="Wärmepumpe installieren lassen ✓ bis zu 70% staatliche Förderung ✓ Festpreisgarantie ✓ lokale Fachpartner. Jetzt kostenloses Angebot in 2 Minuten anfordern."
        path="/"
        jsonLd={faqJsonLd}
      />
      <ExitIntentPopup />
      <Header />

      {/* HERO */}
      <section className="relative pt-32 pb-24 lg:pt-44 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <span className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-brand">
              <Sparkles className="h-3.5 w-3.5" /> Förderfähig 2026
            </span>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-forest">
              Wärmepumpe installieren<br />
              <span className="text-terracotta">mit bis zu 70% Förderung</span>
            </h1>
            <p className="mt-6 text-lg text-[#5A6172] max-w-md leading-relaxed">
              Erhalten Sie Ihr unverbindliches Festpreis-Angebot für die Installation Ihrer
              neuen Wärmepumpe – inklusive bis zu 70% staatlicher Förderung.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/funnel"
                data-testid="hero-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 text-base font-bold text-white shadow-lg shadow-terracotta/30 hover:bg-terracotta-hover hover:-translate-y-0.5 transition-all"
              >
                Jetzt Angebot einholen <ChevronRight className="h-5 w-5" />
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4">
              {[
                { icon: BadgeEuro, t: "Bis zu 70% Förderung" },
                { icon: MapPin, t: "Lokale Fachpartner" },
                { icon: ShieldCheck, t: "Festpreisgarantie" },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-2 text-sm font-semibold text-forest">
                  <b.icon className="h-5 w-5 text-brand" /> {b.t}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-forest/20 aspect-[4/5]">
              <img src={HERO_IMG} alt="Modernes Haus mit Wärmepumpe" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-[#D9DBE0] max-w-[200px]">
              <div className="flex -space-x-2 mb-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-7 w-7 rounded-full bg-brand/80 border-2 border-white" />
                ))}
              </div>
              <p className="text-sm font-bold text-forest">2.400+ Haushalte</p>
              <p className="text-xs text-[#9AA0AC]">vertrauen auf Wärmekraft</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-terracotta">Warum Wärmepumpe</span>
            <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl text-forest tracking-tight">
              Die Vorteile auf einen Blick
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fade}
                className="rounded-2xl border border-[#D9DBE0] bg-bone p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
                data-testid={`benefit-${i}`}
              >
                <div className="h-12 w-12 rounded-xl bg-brand/10 flex items-center justify-center">
                  <b.icon className="h-6 w-6 text-brand" />
                </div>
                <h3 className="mt-4 font-display font-bold text-lg text-forest">{b.title}</h3>
                <p className="mt-2 text-sm text-[#5A6172] leading-relaxed">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div className="rounded-2xl overflow-hidden shadow-xl aspect-[5/4]">
            <img src={ENGINEER_IMG} alt="Techniker installiert Wärmepumpe" className="w-full h-full object-cover" />
          </div>
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-terracotta">So einfach geht's</span>
            <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl text-forest tracking-tight">
              In 3 Schritten zur neuen Heizung
            </h2>
            <div className="mt-10 space-y-8">
              {steps.map((s, i) => (
                <div key={i} className="flex gap-5" data-testid={`step-${i}`}>
                  <div className="shrink-0 h-12 w-12 rounded-xl bg-forest flex items-center justify-center">
                    <s.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg text-forest">{s.title}</h3>
                    <p className="mt-1 text-sm text-[#5A6172] leading-relaxed">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/funnel"
              data-testid="process-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-bold text-white hover:bg-terracotta-hover hover:-translate-y-0.5 transition-all"
            >
              Kostenlose Anfrage starten <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-forest">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-10 items-center mb-14">
            <div className="lg:col-span-2">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-terracotta">Kundenstimmen</span>
              <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl text-white tracking-tight">
                Tausende zufriedene Haushalte
              </h2>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-video hidden lg:block">
              <img src={FAMILY_IMG} alt="Zufriedene Familie" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-7 backdrop-blur-sm" data-testid={`testimonial-${i}`}>
                <Quote className="h-7 w-7 text-terracotta" />
                <p className="mt-4 text-white/90 leading-relaxed text-[15px]">"{t.text}"</p>
                <div className="mt-5 flex items-center gap-1">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="h-4 w-4 fill-terracotta text-terracotta" />
                  ))}
                </div>
                <p className="mt-3 font-bold text-white">{t.name}</p>
                <p className="text-sm text-white/60">{t.city}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ / SEO CONTENT */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-terracotta">Häufige Fragen</span>
          <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl text-forest tracking-tight">
            Wärmepumpe: Förderung, Kosten & Ablauf
          </h2>
          <p className="mt-4 text-[#5A6172] leading-relaxed">
            Alles Wichtige rund um die Installation Ihrer neuen Wärmepumpe – von der
            staatlichen Förderung über die Kosten bis zum Ablauf. Noch Fragen? Fordern
            Sie einfach Ihr kostenloses Angebot an.
          </p>
          <Accordion type="single" collapsible className="mt-8">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-[#D9DBE0]" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-base font-bold text-forest hover:no-underline py-5 text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5A6172] leading-relaxed text-[15px]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-10 rounded-2xl border border-[#D9DBE0] bg-bone p-6">
            <p className="text-sm font-bold text-forest">Wärmepumpe in Ihrer Region</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Link to="/waermepumpe/wuppertal" data-testid="link-wuppertal" className="text-sm font-semibold text-terracotta hover:underline">Wärmepumpe Wuppertal</Link>
              <span className="text-[#9AA0AC]">·</span>
              <Link to="/waermepumpe/duesseldorf" data-testid="link-duesseldorf" className="text-sm font-semibold text-terracotta hover:underline">Wärmepumpe Düsseldorf</Link>
              <span className="text-[#9AA0AC]">·</span>
              <Link to="/waermepumpe/duisburg" data-testid="link-duisburg" className="text-sm font-semibold text-terracotta hover:underline">Wärmepumpe Duisburg</Link>
              <span className="text-[#9AA0AC]">·</span>
              <Link to="/waermepumpe/essen" data-testid="link-essen" className="text-sm font-semibold text-terracotta hover:underline">Wärmepumpe Essen</Link>
              <span className="text-[#9AA0AC]">·</span>
              <Link to="/waermepumpe/koeln" data-testid="link-koeln" className="text-sm font-semibold text-terracotta hover:underline">Wärmepumpe Köln</Link>
              <span className="text-[#9AA0AC]">·</span>
              <Link to="/waermepumpe/dortmund" data-testid="link-dortmund" className="text-sm font-semibold text-terracotta hover:underline">Wärmepumpe Dortmund</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-bone">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-3xl lg:text-5xl text-forest tracking-tight">
            Bereit für günstige, grüne Wärme?
          </h2>
          <p className="mt-5 text-lg text-[#5A6172]">
            Sichern Sie sich jetzt Ihr unverbindliches Festpreis-Angebot. Dauert nur 2 Minuten.
          </p>
          <Link
            to="/funnel"
            data-testid="final-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta px-9 py-4 text-base font-bold text-white shadow-lg shadow-terracotta/30 hover:bg-terracotta-hover hover:-translate-y-0.5 transition-all"
          >
            Jetzt Angebot einholen <ChevronRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <footer className="bg-forest py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="font-display font-extrabold text-xl text-white">Wärme<span className="text-pink">kraft</span></span>
          </div>
          <p className="text-sm text-white/50">© 2026 Wärmekraft. Alle Rechte vorbehalten.</p>
          <Link to="/admin/login" className="text-sm text-white/40 hover:text-white/70 transition-colors" data-testid="footer-admin-link">
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
}
